import { Injectable } from '@angular/core';
import { MatchResult } from '../models';
import { GoogleMapsLoaderService } from './google/google-maps-loader.service';

@Injectable({
  providedIn: 'root'
})
export class MatchingService {
  private autocompleteService: any;
  private placesService: any;
  private streetViewService: any;

  constructor(private mapsLoader: GoogleMapsLoaderService) {
      this.mapsLoader.load().then(() => {
          if (this.mapsLoader.isLoaded) {
              this.autocompleteService = new (window as any).google.maps.places.AutocompleteService();
              this.streetViewService = new (window as any).google.maps.StreetViewService();
              // PlacesService requires a node. Create a dummy one.
              const dummyNode = document.createElement('div');
              this.placesService = new (window as any).google.maps.places.PlacesService(dummyNode);
          }
      });
  }

  async findMatches(query: string, type: 'entity' | 'address' | 'account' | 'provider', locationBias?: { state?: string; city?: string }): Promise<MatchResult[]> {
    if (type === 'address' && this.mapsLoader.isLoaded && this.autocompleteService) {
        return this.findGoogleMatches(query, locationBias);
    }

    // Google Maps not available for address lookups, or non-address type
    return [];
  }

  async getMatchDetails(id: string, type: string): Promise<any> {
      // Real Google Lookup
      if (type === 'address' && this.mapsLoader.isLoaded && this.placesService) {
          return this.getGooglePlaceDetails(id);
      }

      return null;
  }

  // --- Google Implementation ---

  async getVisualForLocation(lat: number, lng: number, fallbackImageUrl?: string): Promise<string> {
      if (!this.mapsLoader.isLoaded || !this.streetViewService) {
          return fallbackImageUrl || '';
      }

      try {
          // Check for Street View
          const panorama: any = await new Promise((resolve, reject) => {
              this.streetViewService.getPanorama({
                  location: { lat, lng },
                  radius: 50
              }, (data: any, status: any) => {
                  if (status === 'OK') resolve(data);
                  else reject(status);
              });
          });

          // Calculate heading to face the building
          const svLocation = panorama.location.latLng;
          const buildingLocation = new (window as any).google.maps.LatLng(lat, lng);
          const heading = (window as any).google.maps.geometry.spherical.computeHeading(svLocation, buildingLocation);

          return `https://maps.googleapis.com/maps/api/streetview?size=400x400&location=${lat},${lng}&fov=90&heading=${heading}&pitch=10&key=${this.mapsLoader.getApiKey()}`;

      } catch {
          // Street View not available, fallback to Static Map
          return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=19&size=400x400&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=${this.mapsLoader.getApiKey()}`;
      }
  }

  private findGoogleMatches(query: string, locationBias?: { state?: string; city?: string }): Promise<MatchResult[]> {
      return new Promise((resolve) => {
          // Build request options
          const requestOptions: any = {
              input: query,
              types: ['address'],
              componentRestrictions: { country: 'us' }
          };

          // Add location bias based on state/city from the bill
          if (locationBias?.state || locationBias?.city) {
              let biasHint = '';
              if (locationBias.city) biasHint += locationBias.city + ', ';
              if (locationBias.state) biasHint += locationBias.state;

              const queryLower = query.toLowerCase();
              const hasState = locationBias.state && queryLower.includes(locationBias.state.toLowerCase());
              const hasCity = locationBias.city && queryLower.includes(locationBias.city.toLowerCase());

              if (!hasState && !hasCity && biasHint) {
                  requestOptions.input = `${query}, ${biasHint}`;
              }
          }

          this.autocompleteService.getPlacePredictions(requestOptions, (predictions: any[], status: any) => {
              if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !predictions) {
                  resolve([]);
                  return;
              }

              const results: MatchResult[] = predictions.map(p => {
                  return {
                      id: p.place_id,
                      label: p.structured_formatting.main_text,
                      subLabel: p.structured_formatting.secondary_text,
                      score: 1.0,
                      type: 'address'
                  };
              });
              resolve(results);
          });
      });
  }

  private getGooglePlaceDetails(placeId: string): Promise<any> {
      return new Promise((resolve) => {
          this.placesService.getDetails({
              placeId: placeId,
              fields: ['address_components', 'formatted_address', 'photos', 'geometry']
          }, (place: any, status: any) => {
              if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !place) {
                  resolve(null);
                  return;
              }

              // Extract components
              let streetNum = '';
              let route = '';
              let city = '';
              let state = '';
              let zip = '';

              place.address_components.forEach((c: any) => {
                  if (c.types.includes('street_number')) streetNum = c.long_name;
                  if (c.types.includes('route')) route = c.long_name;
                  if (c.types.includes('locality')) city = c.long_name;
                  if (c.types.includes('administrative_area_level_1')) state = c.short_name;
                  if (c.types.includes('postal_code')) zip = c.long_name;
              });

              let imageUrl = null;
              if (place.photos && place.photos.length > 0) {
                  imageUrl = place.photos[0].getUrl({ maxWidth: 400, maxHeight: 400 });
              }

              const lat = place.geometry?.location?.lat();
              const lng = place.geometry?.location?.lng();

              resolve({
                  id: placeId,
                  street: `${streetNum} ${route}`.trim(),
                  city,
                  state,
                  zip,
                  imageUrl,
                  lat,
                  lng,
                  full: place.formatted_address
              });
          });
      });
  }
}
